// Feb-2026 · Auditoría PLD/FT 52ª — Configuración de la entidad
// Pantalla ÚNICA con 5 secciones + padrón de empleados como tab interno.
// Extiende el esquema de `entidades` (aditivo, cero migración destructiva) y
// alimenta la próxima iteración: Informe de Auditoría PLD/FT en .docx.
import { useEffect, useState, useCallback, useMemo } from "react";
import {
  Building2, Save, Users, ShieldCheck, ClipboardCheck, Landmark, MapPin,
  Loader2, PlusCircle, Trash2, UploadCloud, Download, AlertTriangle, CheckCircle2, Edit3, XCircle, FileCheck2,
} from "lucide-react";
import { PageHeader } from "@/components/common/PageHeader";
import { DataTable } from "@/components/common/DataTable";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Dialog, DialogContent, DialogHeader, DialogFooter, DialogTitle } from "@/components/ui/dialog";
import { api, apiError, API } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import AutorizacionAuditoriaExterna from "@/components/config/AutorizacionAuditoriaExterna";
import DesembolsoOriginadorSwitch from "@/components/config/DesembolsoOriginadorSwitch";
import RequisitosPorProducto from "@/components/config/RequisitosPorProducto";

const ORGANOS = ["Consejo de Administración", "Administrador Único"];
const ORIGEN_FONDEO_OPCIONES = ["Propio", "Bancario", "Bursátil", "Internacional", "Mixto"];
const MONEDAS_OPCIONES = ["MXN", "USD", "EUR", "CAD", "GBP"];
const PRODUCTOS_OPCIONES = [
  "Crédito", "Crédito PyME", "Crédito Personal", "Crédito con Garantía",
  "Arrendamiento financiero", "Factoraje financiero",
  "Fideicomisos de garantía",
];
const ESTADOS_MX = [
  "Aguascalientes","Baja California","Baja California Sur","Campeche","Chiapas","Chihuahua","CDMX",
  "Coahuila","Colima","Durango","Estado de México","Guanajuato","Guerrero","Hidalgo","Jalisco",
  "Michoacán","Morelos","Nayarit","Nuevo León","Oaxaca","Puebla","Querétaro","Quintana Roo",
  "San Luis Potosí","Sinaloa","Sonora","Tabasco","Tamaulipas","Tlaxcala","Veracruz","Yucatán","Zacatecas",
];

const VigenciaBadge = ({ v }) => (
  v === true ? <Badge className="bg-emerald-100 text-emerald-700" data-testid="badge-vigente">Vigente</Badge>
  : v === false ? <Badge className="bg-red-100 text-red-700" data-testid="badge-vencido">Vencido</Badge>
  : <Badge variant="outline" data-testid="badge-sin-fecha">Sin fecha</Badge>
);

function MultiSelect({ opciones, value = [], onChange, testid }) {
  const set = new Set(value || []);
  return (
    <div className="flex flex-wrap gap-2" data-testid={testid}>
      {opciones.map((o) => {
        const on = set.has(o);
        return (
          <button key={o} type="button" onClick={() => {
            const s = new Set(value || []);
            if (s.has(o)) s.delete(o); else s.add(o);
            onChange(Array.from(s));
          }} className={"rounded-full border px-3 py-1 text-xs " + (on ? "border-forest-600 bg-forest-600 text-white" : "border-slate-300 bg-white text-slate-700")}>
            {o}
          </button>
        );
      })}
    </div>
  );
}

export default function ConfiguracionEntidad() {
  const { user, giro } = useAuth();
  const puedeEditar = ["admin_entidad", "tecnico"].includes(user?.role);
  const puedeVer = ["admin_entidad", "tecnico", "oficial_cumplimiento", "auditor_externo", "super_admin"].includes(user?.role);

  const [entidad, setEntidad] = useState(null);
  const [form, setForm] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [tab, setTab] = useState("identificacion");

  const setF = (k, v) => setForm((p) => ({ ...(p || {}), [k]: v }));
  const setNested = (obj, k, v) => setForm((p) => ({ ...(p || {}), [obj]: { ...(p?.[obj] || {}), [k]: v } }));

  const load = useCallback(async () => {
    if (!user?.entity_id) { setLoading(false); return; }
    try {
      const { data } = await api.get(`/entidades/${user.entity_id}`);
      setEntidad(data);
      // Prepara form con defaults
      setForm({
        clave_casfim: data.clave_casfim || "",
        fecha_alta_sipres: data.fecha_alta_sipres || "",
        domicilio_estructurado: data.domicilio_estructurado || { calle: "", numero_exterior: "", numero_interior: "", colonia: "", codigo_postal: "", municipio: "", estado: "" },
        dictamen_tecnico: data.dictamen_tecnico || { folio: "", fecha_emision: "", fecha_vigencia: "" },
        origen_fondeo: data.origen_fondeo || [],
        sucursales_nacionales: data.sucursales_nacionales ?? "",
        sucursales_extranjero: data.sucursales_extranjero ?? "",
        empleados_directos: data.empleados_directos ?? "",
        empleados_indirectos: data.empleados_indirectos ?? "",
        recibe_efectivo_directo: !!data.recibe_efectivo_directo,
        sabado_habil: !!data.sabado_habil,
        autorizacion_contratos_no_presenciales: !!data.autorizacion_contratos_no_presenciales,
        autorizacion_contratos_np: data.autorizacion_contratos_np || { folio: "", fecha: "" },
        zona_geografica_operacion: data.zona_geografica_operacion || "",
        monedas_operacion: data.monedas_operacion || [],
        productos: data.productos || data.productos_servicios || [],
        mercado_atendido: data.mercado_atendido || "",
        organo_administracion: data.organo_administracion || "",
        consejeros: data.consejeros || [],
        comite_control: data.comite_control || { aplica: null, excepcion_menos_25: false, fecha_integracion_inicial: "", fecha_aviso_cnbv: "", miembros: [] },
        oficial_cumplimiento: {
          ...(data.oficial_cumplimiento || {}),
          certificacion_cnbv: (data.oficial_cumplimiento || {}).certificacion_cnbv || { numero_certificado: "", fecha_emision: "", fecha_vigencia: "" },
          oficio_designacion_cnbv: (data.oficial_cumplimiento || {}).oficio_designacion_cnbv || { folio: "", fecha_aviso: "" },
        },
      });
    } catch (e) {
      toast.error(apiError(e));
    } finally { setLoading(false); }
  }, [user?.entity_id]);
  useEffect(() => { load(); }, [load]);

  const guardar = async () => {
    setSaving(true);
    try {
      const payload = { ...form };
      // Convierte strings vacíos a null en fechas para no ensuciar DB
      const clean = (o, keys) => { keys.forEach((k) => { if (o?.[k] === "") o[k] = null; }); };
      if (payload.dictamen_tecnico) clean(payload.dictamen_tecnico, ["fecha_emision","fecha_vigencia","folio"]);
      if (payload.oficial_cumplimiento?.certificacion_cnbv) clean(payload.oficial_cumplimiento.certificacion_cnbv, ["numero_certificado","fecha_emision","fecha_vigencia"]);
      if (payload.oficial_cumplimiento?.oficio_designacion_cnbv) clean(payload.oficial_cumplimiento.oficio_designacion_cnbv, ["folio","fecha_aviso"]);
      // Números: parseo
      ["sucursales_nacionales","sucursales_extranjero","empleados_directos","empleados_indirectos"].forEach((k) => {
        if (payload[k] === "" || payload[k] === null) payload[k] = null;
        else payload[k] = Number(payload[k]);
      });
      const { data } = await api.put(`/entidades/${user.entity_id}`, payload);
      setEntidad(data);
      toast.success("Configuración guardada.");
      await load();
    } catch (e) { toast.error(apiError(e)); }
    finally { setSaving(false); }
  };

  if (loading) return <div className="p-6 text-sm text-slate-500">Cargando…</div>;
  if (!puedeVer || !form) return <div className="p-6 text-sm text-slate-500">Tu perfil no tiene acceso a la configuración de la entidad.</div>;

  return (
    <div className="space-y-6" data-testid="pagina-configuracion-entidad">
      <PageHeader title="Configuración de la entidad" description="Datos institucionales para el Informe de Auditoría PLD/FT (Disposición 52ª DCG SOFOMES) y el padrón de empleados." />

      <Tabs value={tab} onValueChange={setTab} className="w-full">
        <TabsList className="grid w-full grid-cols-3 md:grid-cols-7">
          <TabsTrigger value="identificacion" data-testid="tab-identificacion"><Building2 className="mr-1 h-4 w-4" /> Identificación</TabsTrigger>
          <TabsTrigger value="dictamen" data-testid="tab-dictamen"><ClipboardCheck className="mr-1 h-4 w-4" /> Dictamen técnico</TabsTrigger>
          <TabsTrigger value="auditoria_externa" data-testid="tab-auditoria-externa"><ShieldCheck className="mr-1 h-4 w-4" /> Auditoría externa PLD/FT</TabsTrigger>
          <TabsTrigger value="operativo" data-testid="tab-operativo"><MapPin className="mr-1 h-4 w-4" /> Perfil operativo</TabsTrigger>
          <TabsTrigger value="gobierno" data-testid="tab-gobierno"><Landmark className="mr-1 h-4 w-4" /> Gobierno corporativo</TabsTrigger>
          <TabsTrigger value="oc" data-testid="tab-oc"><ShieldCheck className="mr-1 h-4 w-4" /> Oficial de Cumplimiento</TabsTrigger>
          <TabsTrigger value="empleados" data-testid="tab-empleados"><Users className="mr-1 h-4 w-4" /> Padrón de empleados</TabsTrigger>
          <TabsTrigger value="requisitos" data-testid="tab-requisitos"><FileCheck2 className="mr-1 h-4 w-4" /> Requisitos por producto</TabsTrigger>
        </TabsList>

        {/* ────────────────────── IDENTIFICACIÓN ────────────────────── */}
        <TabsContent value="identificacion">
          <div className="space-y-4 rounded-lg border border-slate-200 bg-white p-5">
            <div className="rounded-md bg-slate-50 p-3 text-xs text-slate-500">
              Estos datos se muestran en el encabezado del Informe de Auditoría y en toda la interacción regulatoria.
              La razón social y el RFC se editan desde la ficha SIPRES/onboarding (no aquí).
            </div>
            <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
              <div>
                <Label>Razón social (readonly)</Label>
                <Input value={entidad?.razon_social || ""} readOnly data-testid="ident-razon-social" />
              </div>
              <div>
                <Label>RFC (readonly)</Label>
                <Input value={entidad?.rfc || ""} readOnly data-testid="ident-rfc" />
              </div>
              <div>
                <Label>Régimen (readonly)</Label>
                <Input value={entidad?.regimen || ""} readOnly data-testid="ident-regimen" />
              </div>
              <div>
                <Label>Clave SIPRES (readonly)</Label>
                <Input value={entidad?.clave_sipres || ""} readOnly data-testid="ident-sipres" />
              </div>
              <div>
                <Label>Clave CASFIM (CONDUSEF)</Label>
                <Input data-testid="ident-casfim" value={form.clave_casfim} onChange={(e) => setF("clave_casfim", e.target.value)} placeholder="p. ej. CASFIM-FIN-001" disabled={!puedeEditar} />
              </div>
              <div>
                <Label>Fecha de alta en SIPRES</Label>
                <Input type="date" data-testid="ident-fecha-alta-sipres" value={form.fecha_alta_sipres || ""} onChange={(e) => setF("fecha_alta_sipres", e.target.value)} disabled={!puedeEditar} />
              </div>
            </div>

            <h4 className="mt-4 font-heading text-sm font-semibold text-slate-700">Domicilio fiscal (estructurado)</h4>
            <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
              <div><Label>Calle</Label><Input data-testid="dom-calle" value={form.domicilio_estructurado.calle} onChange={(e) => setNested("domicilio_estructurado","calle",e.target.value)} disabled={!puedeEditar} /></div>
              <div><Label>Número exterior</Label><Input data-testid="dom-ext" value={form.domicilio_estructurado.numero_exterior} onChange={(e) => setNested("domicilio_estructurado","numero_exterior",e.target.value)} disabled={!puedeEditar} /></div>
              <div><Label>Número interior</Label><Input data-testid="dom-int" value={form.domicilio_estructurado.numero_interior} onChange={(e) => setNested("domicilio_estructurado","numero_interior",e.target.value)} disabled={!puedeEditar} /></div>
              <div><Label>Colonia</Label><Input data-testid="dom-col" value={form.domicilio_estructurado.colonia} onChange={(e) => setNested("domicilio_estructurado","colonia",e.target.value)} disabled={!puedeEditar} /></div>
              <div><Label>Código postal</Label><Input data-testid="dom-cp" value={form.domicilio_estructurado.codigo_postal} onChange={(e) => setNested("domicilio_estructurado","codigo_postal",e.target.value)} disabled={!puedeEditar} /></div>
              <div><Label>Municipio / alcaldía</Label><Input data-testid="dom-mun" value={form.domicilio_estructurado.municipio} onChange={(e) => setNested("domicilio_estructurado","municipio",e.target.value)} disabled={!puedeEditar} /></div>
              <div>
                <Label>Entidad federativa</Label>
                <select data-testid="dom-estado" value={form.domicilio_estructurado.estado} onChange={(e) => setNested("domicilio_estructurado","estado",e.target.value)} disabled={!puedeEditar}
                  className="w-full rounded-md border border-slate-200 px-2 py-1.5 text-sm">
                  <option value="">—</option>
                  {ESTADOS_MX.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
            </div>
            <p className="text-xs text-slate-400">El campo legacy <code>domicilio</code> como texto libre se conserva y NO se pierde.</p>
          </div>
        </TabsContent>

        {/* ────────────────────── DICTAMEN TÉCNICO ────────────────────── */}
        <TabsContent value="dictamen">
          <div className="rounded-lg border border-slate-200 bg-white p-5">
            <h3 className="font-heading text-base font-semibold text-slate-900">Dictamen técnico PLD/FT</h3>
            <p className="mt-1 text-xs text-slate-500">Documento oficial que emite el auditor externo tras revisar el sistema PLD/FT (52ª A-XV). Su vigencia es indicativa: se recalcula al guardar.</p>
            <div className="mt-4 grid grid-cols-1 gap-3 md:grid-cols-3">
              <div><Label>Folio</Label><Input data-testid="dt-folio" value={form.dictamen_tecnico.folio} onChange={(e) => setNested("dictamen_tecnico","folio",e.target.value)} disabled={!puedeEditar} /></div>
              <div><Label>Fecha de emisión</Label><Input type="date" data-testid="dt-emision" value={form.dictamen_tecnico.fecha_emision || ""} onChange={(e) => setNested("dictamen_tecnico","fecha_emision",e.target.value)} disabled={!puedeEditar} /></div>
              <div><Label>Fecha de vigencia</Label><Input type="date" data-testid="dt-vigencia" value={form.dictamen_tecnico.fecha_vigencia || ""} onChange={(e) => setNested("dictamen_tecnico","fecha_vigencia",e.target.value)} disabled={!puedeEditar} /></div>
            </div>
            <div className="mt-3 flex items-center gap-2 text-sm">
              <span className="text-slate-500">Estatus:</span>
              <VigenciaBadge v={entidad?.dictamen_tecnico?.vigente} />
            </div>
          </div>
        </TabsContent>

        {/* ────────────────────── AUDITORÍA EXTERNA PLD/FT ────────────────────── */}
        <TabsContent value="auditoria_externa">
          <AutorizacionAuditoriaExterna />
        </TabsContent>

        {/* ────────────────────── PERFIL OPERATIVO ────────────────────── */}
        <TabsContent value="operativo">
          {giro === "sofom" && <div className="mb-4"><DesembolsoOriginadorSwitch /></div>}
          <div className="space-y-4 rounded-lg border border-slate-200 bg-white p-5">
            <div>
              <Label>Origen del fondeo</Label>
              <MultiSelect testid="perfil-fondeo" opciones={ORIGEN_FONDEO_OPCIONES} value={form.origen_fondeo} onChange={(v) => setF("origen_fondeo", v)} />
            </div>
            <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
              <div><Label>Sucursales nacionales</Label><Input type="number" min="0" data-testid="perfil-suc-nac" value={form.sucursales_nacionales} onChange={(e) => setF("sucursales_nacionales", e.target.value)} disabled={!puedeEditar} /></div>
              <div><Label>Sucursales en el extranjero</Label><Input type="number" min="0" data-testid="perfil-suc-ext" value={form.sucursales_extranjero} onChange={(e) => setF("sucursales_extranjero", e.target.value)} disabled={!puedeEditar} /></div>
              <div><Label>Empleados directos</Label><Input type="number" min="0" data-testid="perfil-emp-dir" value={form.empleados_directos} onChange={(e) => setF("empleados_directos", e.target.value)} disabled={!puedeEditar} /></div>
              <div><Label>Empleados indirectos</Label><Input type="number" min="0" data-testid="perfil-emp-ind" value={form.empleados_indirectos} onChange={(e) => setF("empleados_indirectos", e.target.value)} disabled={!puedeEditar} /></div>
            </div>
            <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
              <div className="flex items-center gap-3">
                <Switch data-testid="perfil-efectivo" checked={form.recibe_efectivo_directo} onCheckedChange={(v) => setF("recibe_efectivo_directo", v)} disabled={!puedeEditar} />
                <Label>¿Recibe efectivo directamente?</Label>
              </div>
              <div className="flex items-center gap-3">
                <Switch data-testid="perfil-noprop" checked={form.autorizacion_contratos_no_presenciales} onCheckedChange={(v) => setF("autorizacion_contratos_no_presenciales", v)} disabled={!puedeEditar} />
                <Label>Autorización para contratos no presenciales</Label>
              </div>
              <div className="flex items-center gap-3">
                <Switch data-testid="perfil-sabado-habil" checked={form.sabado_habil} onCheckedChange={(v) => setF("sabado_habil", v)} disabled={!puedeEditar} />
                <div>
                  <Label>Cobramos en sábado</Label>
                  <p className="text-[11px] text-slate-500">Los pagos que caigan en sábado se quedan en sábado. Si está apagado, se recorren al lunes. Aplica a créditos nuevos; las tablas ya generadas no cambian.</p>
                </div>
              </div>
            </div>
            {form.autorizacion_contratos_no_presenciales && (
              <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                <div><Label>Folio de autorización</Label><Input data-testid="perfil-noprop-folio" value={form.autorizacion_contratos_np.folio || ""} onChange={(e) => setNested("autorizacion_contratos_np","folio",e.target.value)} disabled={!puedeEditar} /></div>
                <div><Label>Fecha de autorización</Label><Input type="date" data-testid="perfil-noprop-fecha" value={form.autorizacion_contratos_np.fecha || ""} onChange={(e) => setNested("autorizacion_contratos_np","fecha",e.target.value)} disabled={!puedeEditar} /></div>
              </div>
            )}
            <div>
              <Label>Zona geográfica de operación</Label>
              <Textarea data-testid="perfil-zona" value={form.zona_geografica_operacion} onChange={(e) => setF("zona_geografica_operacion", e.target.value)} disabled={!puedeEditar} rows={2} placeholder="Ej. CDMX, Estado de México, Jalisco, Nuevo León" />
            </div>
            <div>
              <Label>Monedas de operación</Label>
              <MultiSelect testid="perfil-monedas" opciones={MONEDAS_OPCIONES} value={form.monedas_operacion} onChange={(v) => setF("monedas_operacion", v)} />
            </div>
          </div>
        </TabsContent>

        {/* ────────────────────── GOBIERNO CORPORATIVO ────────────────────── */}
        <TabsContent value="gobierno">
          <div className="space-y-5 rounded-lg border border-slate-200 bg-white p-5">
            <div>
              <Label>Órgano de administración</Label>
              <div className="flex flex-wrap gap-2" data-testid="gob-organo">
                {ORGANOS.map((o) => (
                  <button key={o} type="button" onClick={() => setF("organo_administracion", o)}
                    className={"rounded-full border px-3 py-1 text-sm " + (form.organo_administracion === o ? "border-forest-600 bg-forest-600 text-white" : "border-slate-300 bg-white")}
                    disabled={!puedeEditar}>{o}</button>
                ))}
              </div>
            </div>

            <ConsejerosEditor value={form.consejeros} onChange={(v) => setF("consejeros", v)} puedeEditar={puedeEditar} />

            <ComiteEditor value={form.comite_control} onChange={(v) => setF("comite_control", v)} puedeEditar={puedeEditar} legacy={entidad?.comite_control?._legacy_integrantes} />
          </div>
        </TabsContent>

        {/* ────────────────────── OFICIAL DE CUMPLIMIENTO ────────────────────── */}
        <TabsContent value="oc">
          <div className="space-y-4 rounded-lg border border-slate-200 bg-white p-5">
            <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
              <div><Label>Nombre</Label><Input data-testid="oc-nombre" value={form.oficial_cumplimiento.nombre || ""} onChange={(e) => setNested("oficial_cumplimiento","nombre",e.target.value)} disabled={!puedeEditar} /></div>
              <div><Label>Correo</Label><Input data-testid="oc-correo" value={form.oficial_cumplimiento.correo || ""} onChange={(e) => setNested("oficial_cumplimiento","correo",e.target.value)} disabled={!puedeEditar} /></div>
              <div><Label>RFC</Label><Input data-testid="oc-rfc" value={form.oficial_cumplimiento.rfc || ""} onChange={(e) => setNested("oficial_cumplimiento","rfc",e.target.value)} disabled={!puedeEditar} /></div>
              <div><Label>Fecha de designación</Label><Input type="date" data-testid="oc-fecha-desig" value={form.oficial_cumplimiento.fecha_designacion || ""} onChange={(e) => setNested("oficial_cumplimiento","fecha_designacion",e.target.value)} disabled={!puedeEditar} /></div>
              <div className="md:col-span-2"><Label>Oficio de designación (interno)</Label><Input data-testid="oc-oficio-int" value={form.oficial_cumplimiento.oficio_cnbv || ""} onChange={(e) => setNested("oficial_cumplimiento","oficio_cnbv",e.target.value)} disabled={!puedeEditar} /></div>
            </div>

            <h4 className="mt-4 font-heading text-sm font-semibold text-slate-700">Certificación CNBV (Art. 4º Bis LFPIORPI)</h4>
            <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
              <div><Label>Número de certificado</Label><Input data-testid="oc-cert-num" value={form.oficial_cumplimiento.certificacion_cnbv.numero_certificado || ""} onChange={(e) => setForm((p) => ({ ...p, oficial_cumplimiento: { ...p.oficial_cumplimiento, certificacion_cnbv: { ...p.oficial_cumplimiento.certificacion_cnbv, numero_certificado: e.target.value } } }))} disabled={!puedeEditar} /></div>
              <div><Label>Fecha de emisión</Label><Input type="date" data-testid="oc-cert-em" value={form.oficial_cumplimiento.certificacion_cnbv.fecha_emision || ""} onChange={(e) => setForm((p) => ({ ...p, oficial_cumplimiento: { ...p.oficial_cumplimiento, certificacion_cnbv: { ...p.oficial_cumplimiento.certificacion_cnbv, fecha_emision: e.target.value } } }))} disabled={!puedeEditar} /></div>
              <div><Label>Fecha de vigencia</Label><Input type="date" data-testid="oc-cert-vig" value={form.oficial_cumplimiento.certificacion_cnbv.fecha_vigencia || ""} onChange={(e) => setForm((p) => ({ ...p, oficial_cumplimiento: { ...p.oficial_cumplimiento, certificacion_cnbv: { ...p.oficial_cumplimiento.certificacion_cnbv, fecha_vigencia: e.target.value } } }))} disabled={!puedeEditar} /></div>
            </div>
            <div className="flex items-center gap-2 text-sm">
              <span className="text-slate-500">Estatus certificación:</span>
              <VigenciaBadge v={entidad?.oficial_cumplimiento?.certificacion_cnbv?.vigente} />
            </div>

            <h4 className="mt-4 font-heading text-sm font-semibold text-slate-700">Oficio de designación CNBV (aviso oficial a la autoridad)</h4>
            <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
              <div><Label>Folio del oficio</Label><Input data-testid="oc-desig-folio" value={form.oficial_cumplimiento.oficio_designacion_cnbv.folio || ""} onChange={(e) => setForm((p) => ({ ...p, oficial_cumplimiento: { ...p.oficial_cumplimiento, oficio_designacion_cnbv: { ...p.oficial_cumplimiento.oficio_designacion_cnbv, folio: e.target.value } } }))} disabled={!puedeEditar} /></div>
              <div><Label>Fecha del aviso</Label><Input type="date" data-testid="oc-desig-fecha" value={form.oficial_cumplimiento.oficio_designacion_cnbv.fecha_aviso || ""} onChange={(e) => setForm((p) => ({ ...p, oficial_cumplimiento: { ...p.oficial_cumplimiento, oficio_designacion_cnbv: { ...p.oficial_cumplimiento.oficio_designacion_cnbv, fecha_aviso: e.target.value } } }))} disabled={!puedeEditar} /></div>
            </div>
          </div>
        </TabsContent>

        {/* ────────────────────── PADRÓN DE EMPLEADOS ────────────────────── */}
        <TabsContent value="empleados">
          <PadronEmpleados puedeEditar={puedeEditar} />
        </TabsContent>

        {/* ────────────────────── REQUISITOS POR PRODUCTO ────────────────────── */}
        <TabsContent value="requisitos">
          <RequisitosPorProducto puedeEditar={puedeEditar} />
        </TabsContent>
      </Tabs>

      {/* Botón guardar (solo tabs de configuración; el padrón tiene su propio flujo) */}
      {tab !== "empleados" && puedeEditar && (
        <div className="sticky bottom-0 flex justify-end border-t border-slate-200 bg-white/95 p-3">
          <Button data-testid="btn-guardar-config" onClick={guardar} disabled={saving} className="bg-forest-600 hover:bg-forest-700">
            {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />}
            Guardar cambios
          </Button>
        </div>
      )}
    </div>
  );
}


function ConsejerosEditor({ value = [], onChange, puedeEditar }) {
  const agregar = () => onChange([...(value || []), { nombre_completo: "", cargo: "", tipo: "Propietario", fecha_designacion: "" }]);
  const set = (i, k, v) => { const n = [...(value || [])]; n[i] = { ...n[i], [k]: v }; onChange(n); };
  const quitar = (i) => onChange((value || []).filter((_, ix) => ix !== i));
  return (
    <div data-testid="consejeros-editor">
      <div className="mb-2 flex items-center justify-between">
        <Label>Consejeros / Administrador Único</Label>
        {puedeEditar && (
          <Button size="sm" variant="outline" onClick={agregar} data-testid="btn-agregar-consejero">
            <PlusCircle className="mr-1 h-4 w-4" /> Agregar
          </Button>
        )}
      </div>
      {(!value || value.length === 0) && <p className="text-xs text-slate-400">Sin consejeros capturados.</p>}
      {(value || []).map((c, i) => (
        <div key={i} className="mb-2 grid grid-cols-1 gap-2 rounded border border-slate-200 p-3 md:grid-cols-5" data-testid={`consejero-row-${i}`}>
          <Input placeholder="Nombre completo" value={c.nombre_completo || ""} onChange={(e) => set(i, "nombre_completo", e.target.value)} disabled={!puedeEditar} />
          <Input placeholder="Cargo" value={c.cargo || ""} onChange={(e) => set(i, "cargo", e.target.value)} disabled={!puedeEditar} />
          <select value={c.tipo || "Propietario"} onChange={(e) => set(i, "tipo", e.target.value)} disabled={!puedeEditar}
            className="rounded border border-slate-200 px-2 py-1.5 text-sm">
            <option>Propietario</option><option>Suplente</option>
          </select>
          <Input type="date" value={c.fecha_designacion || ""} onChange={(e) => set(i, "fecha_designacion", e.target.value)} disabled={!puedeEditar} />
          {puedeEditar && (
            <button type="button" onClick={() => quitar(i)} className="text-xs text-red-600 hover:underline" data-testid={`consejero-quitar-${i}`}>
              <Trash2 className="inline h-4 w-4" /> Quitar
            </button>
          )}
        </div>
      ))}
    </div>
  );
}

function ComiteEditor({ value, onChange, puedeEditar, legacy }) {
  const cc = value || {};
  const set = (k, v) => onChange({ ...cc, [k]: v });
  const setMiembro = (i, k, v) => { const n = [...(cc.miembros || [])]; n[i] = { ...n[i], [k]: v }; set("miembros", n); };
  const agregar = () => set("miembros", [...(cc.miembros || []), { nombre_completo: "", cargo: "", tipo: "Titular", area: "", fecha_designacion: "" }]);
  const quitar = (i) => set("miembros", (cc.miembros || []).filter((_, ix) => ix !== i));
  return (
    <div data-testid="comite-editor">
      <h4 className="font-heading text-sm font-semibold text-slate-700">Comité de Comunicación y Control</h4>
      {legacy && (
        <Alert className="mt-1 border-amber-300 bg-amber-50">
          <AlertTriangle className="h-4 w-4 text-amber-700" />
          <AlertDescription className="text-xs">
            Datos previos detectados y ya cargados como miembros titulares: <strong>{legacy.join(", ")}</strong>. Complementa los cargos y tipos antes de guardar.
          </AlertDescription>
        </Alert>
      )}
      <div className="mt-2 grid grid-cols-1 gap-3 md:grid-cols-3">
        <div className="flex items-center gap-2">
          <Switch data-testid="comite-aplica" checked={!!cc.aplica} onCheckedChange={(v) => set("aplica", v)} disabled={!puedeEditar || cc.excepcion_menos_25} />
          <Label>Aplica el Comité</Label>
        </div>
        <div className="flex items-center gap-2">
          <Switch data-testid="comite-excepcion" checked={!!cc.excepcion_menos_25} onCheckedChange={(v) => set("excepcion_menos_25", v)} disabled={!puedeEditar} />
          <Label>Excepción &lt;25 personas</Label>
        </div>
      </div>
      <div className="mt-3 grid grid-cols-1 gap-3 md:grid-cols-2">
        <div><Label>Fecha de integración inicial</Label><Input type="date" data-testid="comite-fecha-inicial" value={cc.fecha_integracion_inicial || ""} onChange={(e) => set("fecha_integracion_inicial", e.target.value)} disabled={!puedeEditar} /></div>
        <div><Label>Fecha de aviso a CNBV</Label><Input type="date" data-testid="comite-fecha-aviso" value={cc.fecha_aviso_cnbv || ""} onChange={(e) => set("fecha_aviso_cnbv", e.target.value)} disabled={!puedeEditar} /></div>
      </div>
      <div className="mt-3">
        <div className="mb-2 flex items-center justify-between">
          <Label>Miembros del Comité</Label>
          {puedeEditar && !cc.excepcion_menos_25 && (
            <Button size="sm" variant="outline" onClick={agregar} data-testid="btn-agregar-comite">
              <PlusCircle className="mr-1 h-4 w-4" /> Agregar
            </Button>
          )}
        </div>
        {(!cc.miembros || cc.miembros.length === 0) && <p className="text-xs text-slate-400">Sin miembros capturados.</p>}
        {(cc.miembros || []).map((m, i) => (
          <div key={i} className="mb-2 grid grid-cols-1 gap-2 rounded border border-slate-200 p-3 md:grid-cols-6" data-testid={`comite-row-${i}`}>
            <Input placeholder="Nombre completo" value={m.nombre_completo || ""} onChange={(e) => setMiembro(i, "nombre_completo", e.target.value)} disabled={!puedeEditar} />
            <Input placeholder="Cargo" value={m.cargo || ""} onChange={(e) => setMiembro(i, "cargo", e.target.value)} disabled={!puedeEditar} />
            <select value={m.tipo || "Titular"} onChange={(e) => setMiembro(i, "tipo", e.target.value)} disabled={!puedeEditar} className="rounded border border-slate-200 px-2 py-1.5 text-sm">
              <option>Titular</option><option>Suplente</option>
            </select>
            <Input placeholder="Área" value={m.area || ""} onChange={(e) => setMiembro(i, "area", e.target.value)} disabled={!puedeEditar} />
            <Input type="date" value={m.fecha_designacion || ""} onChange={(e) => setMiembro(i, "fecha_designacion", e.target.value)} disabled={!puedeEditar} />
            {puedeEditar && (
              <button type="button" onClick={() => quitar(i)} className="text-xs text-red-600 hover:underline" data-testid={`comite-quitar-${i}`}>
                <Trash2 className="inline h-4 w-4" /> Quitar
              </button>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

function PadronEmpleados({ puedeEditar }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [cargaFile, setCargaFile] = useState(null);
  const [cargando, setCargando] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try { const { data } = await api.get("/empleados"); setRows(data || []); }
    catch (e) { toast.error(apiError(e)); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const abrirNuevo = () => { setEditing({ nombre_completo: "", puesto: "", area: "", correo_electronico: "", telefono: "", fecha_ingreso: "", atencion_al_publico: false, activo: true }); setShowForm(true); };
  const abrirEditar = (r) => { setEditing({ ...r }); setShowForm(true); };
  const guardar = async () => {
    try {
      if (editing.id) await api.put(`/empleados/${editing.id}`, editing);
      else await api.post("/empleados", editing);
      toast.success("Empleado guardado.");
      setShowForm(false); setEditing(null); await load();
    } catch (e) { toast.error(apiError(e)); }
  };
  const darBaja = async (r) => {
    if (!window.confirm(`¿Dar de baja a ${r.nombre_completo}? El registro se conserva 5 años.`)) return;
    try { await api.delete(`/empleados/${r.id}`); toast.success("Empleado dado de baja."); await load(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const descargarPlantilla = async () => {
    try {
      const token = localStorage.getItem("cos_token");
      const res = await fetch(`${API}/empleados/plantilla-excel`, { headers: { Authorization: `Bearer ${token}` } });
      const blob = await res.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob); a.download = "plantilla_empleados.xlsx";
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(a.href);
    } catch (e) { toast.error(apiError(e)); }
  };
  const subirCarga = async () => {
    if (!cargaFile) return;
    setCargando(true);
    try {
      const fd = new FormData(); fd.append("archivo", cargaFile);
      const token = localStorage.getItem("cos_token");
      const res = await fetch(`${API}/empleados/carga-masiva`, { method: "POST", headers: { Authorization: `Bearer ${token}` }, body: fd });
      if (!res.ok) throw new Error((await res.json()).detail || "Error en carga masiva");
      const data = await res.json();
      toast.success(`Carga masiva: ${data.insertados} insertados · ${data.errores?.length || 0} errores`);
      setCargaFile(null); await load();
    } catch (e) { toast.error(e.message); } finally { setCargando(false); }
  };

  return (
    <div className="space-y-4" data-testid="padron-empleados">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <h3 className="font-heading text-base font-semibold text-slate-900">Padrón de empleados</h3>
          <p className="text-xs text-slate-500">Base única para el Informe de Auditoría y el Programa de Capacitación PLD. Marca <em>Atención al público</em> a quienes labor en áreas de atención al público o administración de recursos (52ª A-VIII).</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button size="sm" variant="outline" onClick={descargarPlantilla} data-testid="btn-plantilla">
            <Download className="mr-1 h-4 w-4" /> Plantilla Excel
          </Button>
          {puedeEditar && (<>
            <div className="flex items-center gap-1">
              <input type="file" accept=".xlsx,.xls" onChange={(e) => setCargaFile(e.target.files?.[0] || null)} className="text-xs" data-testid="input-carga-empleados" />
              <Button size="sm" onClick={subirCarga} disabled={!cargaFile || cargando} data-testid="btn-carga-empleados">
                {cargando ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <UploadCloud className="mr-1 h-4 w-4" />}
                Cargar
              </Button>
            </div>
            <Button size="sm" onClick={abrirNuevo} data-testid="btn-nuevo-empleado" className="bg-forest-600 hover:bg-forest-700">
              <PlusCircle className="mr-1 h-4 w-4" /> Nuevo empleado
            </Button>
          </>)}
        </div>
      </div>
      <DataTable testid="table-empleados" rows={rows} loading={loading}
        columns={[
          { key: "nombre_completo", label: "Nombre" },
          { key: "puesto", label: "Puesto" },
          { key: "area", label: "Área" },
          { key: "correo_electronico", label: "Correo" },
          { key: "atencion_al_publico", label: "Atención público", render: (r) => r.atencion_al_publico ? <Badge className="bg-emerald-100 text-emerald-700">Sí</Badge> : <Badge variant="outline">No</Badge> },
          { key: "activo", label: "Activo", render: (r) => r.activo ? <Badge className="bg-emerald-100 text-emerald-700">Activo</Badge> : <Badge className="bg-slate-100 text-slate-500">Baja {r.fecha_baja || ""}</Badge> },
          { key: "acciones", label: "", render: (r) => (
            <div className="flex gap-1">
              <Button size="sm" variant="ghost" onClick={() => abrirEditar(r)} data-testid={`btn-editar-emp-${r.id}`}>
                <Edit3 className="h-4 w-4" />
              </Button>
              {puedeEditar && r.activo !== false && (
                <Button size="sm" variant="ghost" onClick={() => darBaja(r)} data-testid={`btn-baja-emp-${r.id}`}>
                  <XCircle className="h-4 w-4 text-red-600" />
                </Button>
              )}
            </div>
          ) },
        ]}
        emptyLabel="No hay empleados capturados. Usa 'Nuevo empleado' o la carga masiva Excel."
      />

      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent className="max-w-lg" data-testid="dialog-empleado">
          <DialogHeader><DialogTitle>{editing?.id ? "Editar empleado" : "Nuevo empleado"}</DialogTitle></DialogHeader>
          {editing && (
            <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
              <div className="md:col-span-2"><Label>Nombre completo *</Label><Input data-testid="emp-nombre" value={editing.nombre_completo} onChange={(e) => setEditing({ ...editing, nombre_completo: e.target.value })} /></div>
              <div><Label>Puesto</Label><Input data-testid="emp-puesto" value={editing.puesto || ""} onChange={(e) => setEditing({ ...editing, puesto: e.target.value })} /></div>
              <div><Label>Área</Label><Input data-testid="emp-area" value={editing.area || ""} onChange={(e) => setEditing({ ...editing, area: e.target.value })} /></div>
              <div><Label>Correo electrónico</Label><Input data-testid="emp-correo" value={editing.correo_electronico || ""} onChange={(e) => setEditing({ ...editing, correo_electronico: e.target.value })} /></div>
              <div><Label>Teléfono</Label><Input data-testid="emp-tel" value={editing.telefono || ""} onChange={(e) => setEditing({ ...editing, telefono: e.target.value })} /></div>
              <div><Label>Fecha de ingreso</Label><Input type="date" data-testid="emp-ingreso" value={editing.fecha_ingreso || ""} onChange={(e) => setEditing({ ...editing, fecha_ingreso: e.target.value })} /></div>
              <div className="flex items-center gap-2 md:col-span-2">
                <Switch data-testid="emp-atn" checked={!!editing.atencion_al_publico} onCheckedChange={(v) => setEditing({ ...editing, atencion_al_publico: v })} />
                <Label>Labora en atención al público o administración de recursos (52ª A-VIII)</Label>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="ghost" onClick={() => setShowForm(false)}>Cancelar</Button>
            <Button onClick={guardar} data-testid="btn-guardar-emp" className="bg-forest-600 hover:bg-forest-700">Guardar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
